import { X, User, Users, UserCircle } from 'lucide-react';
import { cn } from '@/components/ui/utils';

interface BonusRecipientPillProps {
  name: string;
  role?: string;
  amount: number;
  type: 'owner' | 'external' | 'specialist' | 'operations'; // Support both new and legacy types
  onRemove?: () => void;
  className?: string;
}

export function BonusRecipientPill({
  name,
  role,
  amount,
  type,
  onRemove,
  className
}: BonusRecipientPillProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Map types to colors and icons
  const getTypeStyles = () => {
    switch (type) {
      case 'owner':
        return 'bg-success/15 text-success';
      case 'external':
        return 'bg-warning/15 text-warning';
      case 'specialist':
        return 'bg-info/15 text-info';
      case 'operations':
        return 'bg-primary/10 text-primary';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'owner':
        return <Users className="h-3.5 w-3.5" />;
      case 'external':
        return <UserCircle className="h-3.5 w-3.5" />;
      case 'specialist':
        return <User className="h-3.5 w-3.5" />;
      case 'operations':
        return <Users className="h-3.5 w-3.5" />;
      default:
        return <User className="h-3.5 w-3.5" />;
    }
  };

  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm',
        getTypeStyles(),
        className
      )}
    >
      {getIcon()}
      <span className="font-medium">{name}</span>
      {role && (
        <span className="text-xs opacity-75">({role})</span>
      )}
      <span className="font-semibold">{formatCurrency(amount)}</span>
      {onRemove && (
        <button
          onClick={onRemove}
          className="ml-1 hover:opacity-70 transition-opacity"
          aria-label={`Remove ${name}`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
